import { useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { ArrowLeft, Image as ImageIcon, Package, FileImage, Upload, Save } from 'lucide-react';
import type { CpGpMatchedProduct } from '../../../../../models/Coupang';
import { useImageEditor, useMatchedProductData, useImageUpload } from '../../../../../apps/hooks/media';
import { useImagePublish } from '../../../../../apps/hooks/media/useImagePublish';
import { ImageEditorCanvas } from './components/ImageEditorCanvas';
import { ImageSourcePanel } from './components/ImageSourcePanel';
import { PublishedImagePanel } from './components/PublishedImagePanel';
import type { ImageEditMode, ImageEditorTool } from './types';
import { useImageInitializer } from './hooks/useImageInitializer';
import type { PDPImageEditorRef } from './components/PDPImageEditor';

const ImageEditorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cpProductId, cpItemId, tab } = useParams<{ cpProductId: string; cpItemId: string; tab?: string }>();
  const [editMode, setEditMode] = useState<ImageEditMode>(tab === 'pdp' ? 'pdp' : 'thu');
  const [selectedTool, setSelectedTool] = useState<ImageEditorTool>('select');
  const [textSettings, setTextSettings] = useState({ color: '#000000', fontSize: 18 });
  const pdpEditorRef = useRef<PDPImageEditorRef>(null);
  const [pdpSaveLoading, setPdpSaveLoading] = useState(false);
  const [pdpSaveMessage, setPdpSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const productDataFromState = useMemo(() => {
    return location.state?.productData as CpGpMatchedProduct | undefined;
  }, [location.state?.productData]);

  // Data fetching
  const {
    matchedProduct,
    matchLoading,
    matchError,
    cmWorkflowData,
    productsBySku,
    verifiedImages,
    refetchVerifiedImages,
    refetchProductsBySku,
  } = useMatchedProductData({
    cpProductId,
    cpItemId,
    productDataFromState,
  });

  // Image editor state
  const {
    imageSelection,
    setImageSelection,
    deletedDetailedImages,
    setDeletedDetailedImages,
    isInitialLoad,
    setIsInitialLoad,
    hasUnsavedChanges,
    originalThumbnailsRef,
    originalGalleryRef,
    originalPdpImagesRef,
  } = useImageEditor();

  // Image upload
  const { uploadThumbnail } = useImageUpload(matchedProduct);

  // Helper to get SKU from cpItemId
  const getSKUFromCpItemId = useMemo(() => {
    return (cpItemId: string): string | undefined => {
      if (!cmWorkflowData || cmWorkflowData.length === 0) {
        return undefined;
      }
      const skuCpiiMappings = cmWorkflowData[0]?.skuCpiiMappings || [];
      const mapping = skuCpiiMappings.find((m: any) => m.cpii === cpItemId);
      return mapping?.sku;
    };
  }, [cmWorkflowData]);

  // Create productKey to SKU mapping for publishing
  const productKeyToSkuMap = useMemo(() => {
    const map: Record<string, string> = {};
    const restructuredProducts = matchedProduct?.ssRestructuredProducts || [];

    restructuredProducts
      .filter((p: any) => p.type === 'variable' || p.type === 'simple')
      .forEach((product: any) => {
        const productKey = `${product.cpItemId || ''}_${product.type}_${product.attributePack}_${product.attributeSize}_${product.attributeColor}`;
        const sku = getSKUFromCpItemId(product.cpItemId || '');
        if (sku) {
          map[productKey] = sku;
        }
      });

    return map;
  }, [matchedProduct?.ssRestructuredProducts, getSKUFromCpItemId]);

  // Publish to WordPress
  const { publishToWordPress, deletePdpImage, publishLoading, publishMessage, recentlyPublishedSkus, publishingSkus } =
    useImagePublish({
      matchedProduct,
      imageSelection,
      refetchVerifiedImages,
      refetchProductsBySku,
      productsBySku,
      productKeyToSkuMap,
      editMode,
    });

  // Initialize images from verified images
  useImageInitializer({
    verifiedImages,
    isInitialLoad,
    matchedProduct,
    onInitialized: (selection) => {
      originalThumbnailsRef.current = selection.thumbnails;
      originalGalleryRef.current = selection.gallery;
      originalPdpImagesRef.current = selection.pdpImages;
      setImageSelection(selection);
      setIsInitialLoad(false);
    },
  });

  const handleUploadThumbnailImmediately = async (
    dataUrl: string,
    productKey: string,
    cpItemId: string,
  ): Promise<string> => {
    if (!matchedProduct) {
      throw new Error('No matched product available');
    }

    // Upload to S3 and update VerifiedImages
    const cloudFrontUrl = await uploadThumbnail(dataUrl, cpItemId);

    // Update RestructuredProduct.featuredThumbnailUrl
    const restructuredProducts = matchedProduct?.ssRestructuredProducts || [];
    const matchingProduct = restructuredProducts.find((p: any) => p.cpItemId === cpItemId);

    if (matchingProduct) {
      const { sourcingClient } = await import('../../../../../infrastructure/SourcingClient');

      await sourcingClient.updateRestructuredProducts({
        gpProductId: matchedProduct.gpProductId,
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
        restructuredProducts: [
          {
            cpItemId,
            featuredThumbnailUrl: cloudFrontUrl,
            sku: matchingProduct.sku || '',
          },
        ],
      });
    }

    // Update refs to prevent unsaved changes warning
    originalThumbnailsRef.current = {
      ...originalThumbnailsRef.current,
      [productKey]: cloudFrontUrl,
    };

    return cloudFrontUrl;
  };

  const handleSaveEditedImage = (originalImageUrl: string, editedImageUrl: string) => {
    const updatedPdpImages = imageSelection.pdpImages.map((url) => (url === originalImageUrl ? editedImageUrl : url));
    setImageSelection({
      ...imageSelection,
      pdpImages: updatedPdpImages,
    });
  };

  const handleDeletePdpImage = async (url: string) => {
    await deletePdpImage(url);
    const updatedPdpImages = imageSelection.pdpImages.filter((img) => img !== url);
    setImageSelection({
      ...imageSelection,
      pdpImages: updatedPdpImages,
    });
  };

  const handleSavePdpChanges = async () => {
    if (!pdpEditorRef.current) return;

    setPdpSaveLoading(true);
    setPdpSaveMessage(null);

    try {
      await pdpEditorRef.current.savePdpImages();
      // Get the message from the ref after save completes
      setTimeout(() => {
        if (pdpEditorRef.current?.saveMessage) {
          setPdpSaveMessage(pdpEditorRef.current.saveMessage);
        }
        setPdpSaveLoading(false);
      }, 100);
    } catch (error) {
      setPdpSaveMessage({ type: 'error', text: 'Failed to save PDP images' });
      setPdpSaveLoading(false);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave without saving?');
      if (!confirmLeave) {
        return;
      }
    }
    navigate(`/sourcing/matched/${cpProductId}/${cpItemId}`, {
      state: { productData: matchedProduct },
    });
  };

  const handleTabChange = (mode: ImageEditMode) => {
    setEditMode(mode);
    navigate(`/sourcing/matched/${cpProductId}/${cpItemId}/image-editor/${mode}`, { replace: true });
  };

  if (matchLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
        }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} className="mb-3" />
          <p className="text-muted">Loading image editor...</p>
        </div>
      </div>
    );
  }

  if (matchError || !matchedProduct) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
        }}
      >
        <div className="text-center">
          <Package size={64} className="text-muted mb-3" />
          <h5>Error Loading Product</h5>
          <p className="text-muted mb-4">{matchError instanceof Error ? matchError.message : 'Product not found'}</p>
          <Button variant="primary" onClick={handleBack}>
            <ArrowLeft size={16} className="me-2" />
            Back to Product
          </Button>
        </div>
      </div>
    );
  }

  const restructuredProducts = matchedProduct.ssRestructuredProducts || [];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f8f9fa' }}>
      {/* Top Bar */}
      <div
        style={{
          background: 'white',
          borderBottom: '1px solid #dee2e6',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <Button variant="outline-secondary" size="sm" onClick={handleBack}>
          <ArrowLeft size={16} className="me-2" />
          Back
        </Button>

        <div style={{ borderLeft: '1px solid #dee2e6', height: '24px' }} />

        <div className="d-flex align-items-center gap-2">
          <ImageIcon size={18} />
          <h6 className="mb-0">Image Editor</h6>
        </div>

        <div style={{ borderLeft: '1px solid #dee2e6', height: '24px' }} />

        {/* Edit Mode Tabs */}
        <div className="d-flex gap-2">
          <div
            onClick={() => handleTabChange('thu')}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '6px',
              background: editMode === 'thu' ? '#e7f3ff' : 'transparent',
              border: editMode === 'thu' ? '2px solid #0d6efd' : '2px solid #dee2e6',
              color: editMode === 'thu' ? '#0d6efd' : '#6c757d',
              fontWeight: editMode === 'thu' ? 600 : 400,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.875rem',
            }}
          >
            <Package size={16} />
            <span>Thumbnail Images</span>
          </div>

          <div
            onClick={() => handleTabChange('pdp')}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '6px',
              background: editMode === 'pdp' ? '#e7f3ff' : 'transparent',
              border: editMode === 'pdp' ? '2px solid #0d6efd' : '2px solid #dee2e6',
              color: editMode === 'pdp' ? '#0d6efd' : '#6c757d',
              fontWeight: editMode === 'pdp' ? 600 : 400,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.875rem',
            }}
          >
            <FileImage size={16} />
            <span>PDP Images</span>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {editMode === 'pdp' && (
          <Button
            variant="success"
            size="sm"
            onClick={handleSavePdpChanges}
            disabled={pdpSaveLoading || !imageSelection.pdpImages.length}
            className="me-2"
          >
            {pdpSaveLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" style={{ width: '14px', height: '14px' }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} className="me-2" />
                Save PDP Changes
              </>
            )}
          </Button>
        )}

        <Button variant="primary" size="sm" onClick={publishToWordPress} disabled={publishLoading}>
          {publishLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Publishing...
            </>
          ) : (
            <>
              <Upload size={14} className="me-2" />
              Publish to WP
            </>
          )}
        </Button>
      </div>

      {/* Save PDP Message */}
      {pdpSaveMessage && (
        <Alert
          variant={pdpSaveMessage.type === 'success' ? 'success' : 'danger'}
          className="mb-0 rounded-0"
          dismissible
          onClose={() => setPdpSaveMessage(null)}
        >
          {pdpSaveMessage.text}
        </Alert>
      )}

      {/* Publish Message */}
      {publishMessage && (
        <Alert
          variant={publishMessage.type === 'success' ? 'success' : 'danger'}
          className="mb-0 rounded-0"
          dismissible
        >
          {publishMessage.text}
        </Alert>
      )}

      {/* Main Editor Layout */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', gap: '20px', padding: '0px' }}>
        <ImageSourcePanel
          matchedProduct={matchedProduct}
          editMode={editMode}
          selectedTool={selectedTool}
          onToolChange={setSelectedTool}
          verifiedImages={verifiedImages}
          productsBySku={productsBySku}
          textSettings={textSettings}
          onTextSettingsChange={(updates) => setTextSettings({ ...textSettings, ...updates })}
        />

        <ImageEditorCanvas
          matchedProduct={matchedProduct}
          restructuredProducts={restructuredProducts}
          editMode={editMode}
          imageSelection={imageSelection}
          onImageSelectionChange={setImageSelection}
          selectedTool={selectedTool}
          onSaveEditedImage={handleSaveEditedImage}
          textSettings={textSettings}
          deletedDetailedImages={deletedDetailedImages}
          onDeleteDetailedImage={(url) => setDeletedDetailedImages(new Set(deletedDetailedImages).add(url))}
          onDeletePdpImage={handleDeletePdpImage}
          getSKUFromCpItemId={getSKUFromCpItemId}
          onUploadThumbnailImmediately={handleUploadThumbnailImmediately}
          originalGalleryRef={originalGalleryRef}
          verifiedImages={verifiedImages}
          refetchVerifiedImages={async () => {
            await refetchVerifiedImages();
          }}
          pdpEditorRef={pdpEditorRef}
        />

        <PublishedImagePanel
          editMode={editMode}
          productsBySku={productsBySku}
          onRefetchProducts={refetchProductsBySku}
          recentlyPublishedSkus={recentlyPublishedSkus}
          publishingSkus={publishingSkus}
        />
      </div>
    </div>
  );
};

export default ImageEditorPage;
